import {
    emptyTableState,
    getServerUserDisplayName,
    getStatusColor,
    getUserStatusLabel,
    IServerApplication,
    IServerJob,
    isValidString,
    JobStatus,
    TableIds,
    IServerPaginatedResponse,
    ITableForm,
    getColorByEvaluatedScore,
    IColorsObject,
    JobApplicationsTableFilterIds,
    HiringSourcesTableFilterIds,
    getJobsStatusLabel,
    getJobApplicationStatusLabel,
    IServerDashboardJob,
    JobApplicationStatuses,
    ITag,
    IServerSourceOfHireTableRow,
    IServerUserTableRow,
    UserStatus,
} from '../../../common';
import {
    IActiveFilter,
    ISort,
    ITableState,
    MenuActionIds,
    SortDirections,
} from '../../../base';
import { createFilterObjectFromQueryParams } from '../redux/utils';
import i18next from 'i18next';
import moment from 'moment';

export const formatTableState = (
    tableId: string,
    queryParams: Record<string, string>,
    currentEntitiesCount: number,
    responseData: IServerPaginatedResponse
): ITableState => {
    const formattedTableState = emptyTableState(tableId);
    if (queryParams.search) {
        formattedTableState.search = queryParams.search;
    }
    if (queryParams.ordering) {
        const isDesc = queryParams.ordering.startsWith('-');
        const sortKey = isDesc
            ? queryParams.ordering.slice(1)
            : queryParams.ordering;
        const sortObject: ISort = {
            sortDirection: isDesc ? SortDirections.desc : SortDirections.asc,
            sortKey,
        };
        formattedTableState.sort = sortObject;
    }
    formattedTableState.currentEntitiesCount = currentEntitiesCount;
    formattedTableState.initialLoad = false;
    formattedTableState.totalEntries = responseData.total_count;
    formattedTableState.currentPage = responseData.current_page;
    formattedTableState.totalPages = responseData.total_pages;

    formattedTableState.filters =
        createFilterObjectFromQueryParams(queryParams);

    return formattedTableState;
};

export const mapResponseByTableId = (
    tableId: string,
    data: unknown,
    baseColors: IColorsObject
) => {
    let formattedData: Array<Record<string, string | number | null>> = [];
    switch (tableId) {
        case TableIds.JOBS:
            formattedData = formatJobsTableData(data as Array<IServerJob>);
            break;
        case TableIds.JOB_APPLICATIONS:
        case TableIds.SAVED_FOR_LATER:
            formattedData = formatJobApplicationsTableData(
                data as Array<IServerApplication>,
                baseColors
            );
            break;
        case TableIds.DASHBOARD_JOBS:
            formattedData = formatDashboardJobsTableData(
                data as Array<IServerDashboardJob>
            );
            break;
        case TableIds.HIRING_SOURCES:
            formattedData = formatHiringSourcesTableData(
                data as Array<IServerSourceOfHireTableRow>
            );
            break;
        case TableIds.TEAM_MEMBERS:
            formattedData = formatTeamMembersTableData(
                data as Array<IServerUserTableRow>
            );
            break;
        default:
            break;
    }
    return formattedData;
};

const formatHiringSourcesTableData = (
    data: Array<IServerSourceOfHireTableRow>
): Array<Record<string, string | number | null>> => {
    return (
        data?.map((row) => ({
            id: row.sourceId,
            source: row.source_title,
            clicks: String(row.clicks),
            applications: String(row.applications),
            screened: String(row.screened),
            hired: String(row.hired),
            clickApply: `${row.click_apply_percent}%`,
            applyScreen: `${row.apply_screen_percent}%`,
            overallConv: `${row.overall_conversion}%`,
        })) || []
    );
};

export const formatQueryParams = (
    tableState: ITableState,
    page: number,
    formData?: ITableForm
): Record<string, string> => {
    const { defaultSortKey } = tableState;
    const search = formData ? formData.search : tableState.search;
    const sort = formData ? formData.sort : tableState.sort;
    const filters = formData ? formData.filters : tableState.filters;
    const formattedParams: Record<string, string> = {};

    if (isValidString(search)) {
        if (tableState.id === TableIds.HIRING_SOURCES) {
            formattedParams.source_title = search;
        } else {
            formattedParams.search = search;
        }
    }

    formattedParams.ordering = formatSortQueryParam(sort, defaultSortKey);

    if (Array.isArray(filters)) {
        filters?.forEach((filter: IActiveFilter) => {
            const { filterId, values, booleanValue } = filter;
            if (filterId && (values || booleanValue)) {
                switch (filterId) {
                    case JobApplicationsTableFilterIds.score:
                        if (values.length > 0) {
                            if (values[0]) {
                                let minScore = (Number(values[0]) - 1) * 10;
                                minScore = minScore === 0 ? 0 : minScore + 1;
                                formattedParams[
                                    JobApplicationsTableFilterIds.minScore
                                ] = minScore.toString();
                            }
                            if (values[1]) {
                                formattedParams[
                                    JobApplicationsTableFilterIds.maxScore
                                ] = values[1].toString() + '0';
                            }
                        }
                        break;
                    case JobApplicationsTableFilterIds.isSavedForLater:
                        if (booleanValue !== undefined) {
                            formattedParams[filterId] = booleanValue.toString();
                        }
                        break;
                    case JobApplicationsTableFilterIds.status:
                    case JobApplicationsTableFilterIds.jobId:
                    case JobApplicationsTableFilterIds.minScore:
                    case JobApplicationsTableFilterIds.maxScore:
                         const formattedValues = Array.isArray(values)
                            ? values.map((v) => v.value).join(',')
                            : values;
                        formattedParams[filterId] = formattedValues;
                        break;
                    case JobApplicationsTableFilterIds.publishedDate:
                        if (values.length > 0) {
                            if (values[0]) {
                                formattedParams[
                                    JobApplicationsTableFilterIds.dateFrom
                                ] = moment(values[0]).format('DD-MM-YYYY');
                            }
                            if (values[1]) {
                                formattedParams[
                                    JobApplicationsTableFilterIds.dateTo
                                ] = moment(values[1]).format('DD-MM-YYYY');
                            }
                        }
                        break;
                    case JobApplicationsTableFilterIds.tags:
                        if (values.length > 0) {
                            formattedParams[filterId] = values
                                .map((tag: ITag) => tag.tag_id)
                                .join(',');
                        }
                        break;
                    case HiringSourcesTableFilterIds.clicks:
                    case HiringSourcesTableFilterIds.applications:
                    case HiringSourcesTableFilterIds.screened:
                    case HiringSourcesTableFilterIds.hired:
                    case HiringSourcesTableFilterIds.clickApplyPercent:
                    case HiringSourcesTableFilterIds.applyScreenPercent:
                    case HiringSourcesTableFilterIds.overallConversion: {
                        const [min, max] = values as [
                            number | null,
                            number | null,
                        ];
                        if (min !== null && min !== undefined) {
                            formattedParams[`${filterId}_min`] = String(min);
                        }
                        if (max !== null && max !== undefined) {
                            formattedParams[`${filterId}_max`] = String(max);
                        }
                        break;
                    }
                    default:
                        if (Array.isArray(values) && values.length > 0) {
                            const first = values[0];
                            if (
                                first &&
                                typeof first === 'object' &&
                                'value' in first
                            ) {
                                formattedParams[filterId] = values
                                    .map((v: { value: string }) => v.value)
                                    .join(',');
                            } else {
                                formattedParams[filterId] = values.join(',');
                            }
                        }
                        break;
                }
            }
        });
    } else {
        Object.keys(filters).forEach((filter) => {
            const values = filters[filter];
            const formattedValues = Array.isArray(values)
                ? values.join(',')
                : values;
            formattedParams[filter] = formattedValues;
        });
    }

    // Add toggle parameters
    if (formData?.toggles) {
        Object.entries(formData.toggles).forEach(([key, value]) => {
            formattedParams[key] = value.toString();
        });
    }

    // Add page parameter
    formattedParams.page = String(page);

    return formattedParams;
};

const formatSortQueryParam = (sort: ISort, defaultSortKey: string): string => {
    let sortQueryParam = `-${defaultSortKey}`;
    if (isValidString(sort?.sortDirection)) {
        if (sort.sortDirection === SortDirections.desc) {
            sortQueryParam = `-${sort.sortKey}`;
        } else {
            sortQueryParam = sort.sortKey || '';
        }
    }
    return sortQueryParam;
};

export const formatJobsTableData = (
    data: Array<IServerJob>
): Array<Record<string, string | number | null>> => {
    return (
        data?.map((job) => {
            const isLegacy = job.status === JobStatus.Legacy;

            const menuActions = isLegacy
                ? [
                      {
                          label: i18next.t('jobsTable.cloneJob'),
                          action: MenuActionIds.Clone,
                          disabled: false,
                      },
                  ]
                : [
                      ...(job.status === JobStatus.Draft
                          ? [
                                {
                                    label: i18next.t('jobsTable.publishJob'),
                                    action: MenuActionIds.Publish,
                                },
                                {
                                    label: i18next.t('jobsTable.deleteJob'),
                                    action: MenuActionIds.Delete,
                                },
                            ]
                          : []),
                      {
                          label: i18next.t('jobsTable.editJob'),
                          action: MenuActionIds.Edit,
                          disabled:
                              job.status === JobStatus.Closed ||
                              job.status === JobStatus.Completed ||
                              job.status === JobStatus.Canceled,
                      },
                      {
                          label:
                              job.status === JobStatus.Suspended
                                  ? i18next.t('jobsTable.resumeJob')
                                  : i18next.t('jobsTable.suspendJob'),
                          action:
                              job.status === JobStatus.Suspended
                                  ? MenuActionIds.Resume
                                  : MenuActionIds.Suspend,
                          disabled:
                              job.status === JobStatus.Completed ||
                              job.status === JobStatus.Canceled ||
                              job.status === JobStatus.Draft,
                      },
                      {
                          label: i18next.t('jobsTable.cloneJob'),
                          action: MenuActionIds.Clone,
                      },
                      {
                          label: i18next.t('jobsTable.cancelJob'),
                          action: MenuActionIds.Cancel,
                          disabled:
                              job.status === JobStatus.Canceled ||
                              job.status === JobStatus.Completed ||
                              job.status === JobStatus.Draft,
                      },
                      {
                          label: i18next.t('jobsTable.markAsComplete'),
                          action: MenuActionIds.Complete,
                          disabled:
                              job.status === JobStatus.Closed ||
                              job.status === JobStatus.Completed ||
                              job.status === JobStatus.Draft ||
                              job.status === JobStatus.Canceled,
                      },
                  ];

            return {
                id: job.id.toString(),
                externalJobId: job.external_job_id,
                title: job.title,
                publishedDate: job.published_date ?? null,
                statusLabel: getJobsStatusLabel(job.status),
                statusColor: getStatusColor(job.status),
                recruiterName: job.recruiter
                    ? getServerUserDisplayName(job.recruiter)
                    : null,
                recruiterIcon: null,
                recruitingManagerName: job.recruiting_manager
                    ? getServerUserDisplayName(job.recruiting_manager)
                    : null,
                recruitingManagerIcon: '',
                menuActions: menuActions,
                appliedTags: job.tags ?? [],
                modelName: 'job',
                isLegacy,
            };
        }) || []
    );
};

const formatTeamMembersTableData = (
    data: Array<IServerUserTableRow>
): Array<Record<string, string | number | null>> => {
    return (
        data?.map((user) => {
            const menuActions = [
                {
                    label: i18next.t('teamMembersTable.editUser'),
                    action: MenuActionIds.Edit,
                    disabled: false,
                },
                {
                    label: i18next.t('teamMembersTable.removeUser'),
                    action: MenuActionIds.Delete,
                    disabled: false,
                },
                ...(user.status === UserStatus.Invited
                    ? [
                          {
                              label: i18next.t(
                                  'teamMembersTable.resendInvite'
                              ),
                              action: MenuActionIds.ResendInvite,
                              disabled: false,
                          },
                      ]
                    : []),
            ];

            return {
                id: user.id.toString(),
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                icon: user.image_url,
                jobTitle: user.job_title,
                email: user.email,
                role: null,
                statusLabel: getUserStatusLabel(user.status),
                statusColor: getStatusColor(user.status),
                menuActions,
            };
        }) || []
    );
};

const calculateCandidateScore = (score: number) => {
    if (score === 0) {
        return 1;
    }
    return Math.ceil(score / 10);
};


export const formatJobApplicationTableRow = (
    jobApplication: IServerApplication,
    baseColors?: IColorsObject
): Record<string, string | number | null | boolean | unknown> => {
    const isLegacy = jobApplication.is_legacy ?? false;
    const isActiveApplication =
        jobApplication.status === JobApplicationStatuses.inReview ||
        jobApplication.status === JobApplicationStatuses.new;
    const isTransferableApplication =
        jobApplication.status !== JobApplicationStatuses.hired;
    const menuActions = [
        {
            label: i18next.t('jobApplicationsTable.rejectCandidate'),
            action: MenuActionIds.Reject,
            disabled: isLegacy || !isActiveApplication,
        },
        {
            label: i18next.t('jobApplicationsTable.transferCandidate'),
            action: MenuActionIds.Transfer,
            disabled: !isTransferableApplication,
        },
        {
            label: i18next.t('jobApplicationsTable.withdrawCandidate'),
            action: MenuActionIds.Withdraw,
            disabled: isLegacy || !isActiveApplication,
        },
    ];

    const candidateScoreCalculated =
        jobApplication.candidate_score !== null
            ? calculateCandidateScore(jobApplication.candidate_score)
            : null;

    return {
        id: jobApplication.id,
        name: `${jobApplication.first_name} ${jobApplication.last_name}`,
        title: jobApplication.job.title,
        isSavedIconName: jobApplication.is_saved
            ? 'BookmarkFilledIcon'
            : 'BookmarkButtonIcon',
        isSavedIconColor: 'secondary',
        isSavedIconSize: 15,
        rangeBlockFrom: jobApplication.candidate_score > 0 ? 1 : 0,
        rangeBlockTo: candidateScoreCalculated,
        rangeBlockColor: baseColors
            ? getColorByEvaluatedScore(candidateScoreCalculated, baseColors)
            : '',
        matchScore: jobApplication.candidate_score,
        stepId: jobApplication.current_step,
        rangeBlockText: '',
        isLegacy,
        statusLabel: getJobApplicationStatusLabel(jobApplication.status),
        statusColor: getStatusColor(jobApplication.status),
        applicationDate: jobApplication.submission_date,
        menuActions: menuActions,
        appliedTags: jobApplication.tags,
        source: jobApplication.referral_source ?? null,
        modelName: 'job_application',
    };
};

const formatJobApplicationsTableData = (
    data: Array<IServerApplication>,
    baseColors: IColorsObject
): Array<Record<string, string | number>> => {
    return (
        data?.map((jobApplication) =>
            formatJobApplicationTableRow(jobApplication, baseColors)
        ) || []
    );
};

const formatDashboardJobsTableData = (
    data: Array<IServerDashboardJob>
): Array<Record<string, string | number | null>> => {
    return (
        data?.map((job) => {
            return {
                id: job.id.toString(),
                title: job.title,
                publishedDate: job.published_date ?? null,
                openDays: `${job.open_period} ${i18next.t('dashboardJobsTable.days')}`,
                inProgressApplications: job.in_process.toString(),
                totalApplications: job.total_candidates.toString(),
            };
        }) || []
    );
};
